import { create } from 'zustand';
import { User } from '../types';
import { apiPost, apiGet } from '../api/http';
import { useCompanyStore } from './companyStore';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  /** A sessão salva (token) não pôde ser recarregada do servidor (erro/rede) — diferente de "sem permissão" */
  sessionError: boolean;
  setSessionError: (v: boolean) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  sessionError: false,
  setSessionError: (v: boolean) => set({ sessionError: v }),
  login: async (email: string, password: string) => {
    const res = await apiPost('/login', {
      email,
      password,
    });
    // Persist token for API calls
    if (res?.token) {
      localStorage.setItem('token', res.token);
    }

    // Representante/vendedor: não tem empresa nem ERP -- só o Portal Comercial.
    const accountType = res.account_type || 'tenant';
    localStorage.setItem('account_type', accountType);
    if (accountType !== 'tenant') {
      set({
        user: {
          id: String(res.user_id),
          name: res.user_name || 'Usuário',
          email,
          role: res.user_role || 'user',
          is_owner: 0,
          is_superadmin: 0,
          account_type: accountType,
          lock_timeout_minutes: 0,
          profile_name: accountType === 'partner' ? 'Representante' : 'Vendedor',
          permissions: [],
        } as User,
        isAuthenticated: true,
      });
      return;
    }
    
    // Fetch full company data after successful login
    let fullCompany = null;
    try {
      const companyRes = await apiGet('/companies/my-company');
      fullCompany = companyRes?.company;
    } catch (e) {
      console.error('Failed to fetch full company data during login');
    }

    // Update company store with logged-in company info
    useCompanyStore.getState().setCompany({
      id: String(res.company_id || '1'),
      name: fullCompany?.name || res.company_name || 'Sua Empresa',
      document: fullCompany?.document || '',
      email: fullCompany?.email || '',
      phone: fullCompany?.phone || '',
      address: fullCompany ? `${fullCompany.street || ''}, ${fullCompany.number || ''} ${fullCompany.complement || ''} - ${fullCompany.city || ''}, ${fullCompany.state || ''}` : '',
      state: fullCompany?.state || '',
      logo: fullCompany?.logo || res.company_logo || '/company_logo_placeholder.png',
      settings: fullCompany?.settings ? (typeof fullCompany.settings === 'string' ? JSON.parse(fullCompany.settings) : fullCompany.settings) : (res.company_settings || {}),
      subscription: fullCompany?.subscription || res.subscription || {
        plan: 'Nenhum',
        status: 'active',
        expiresAt: null,
        features: [],
      },
    });

    set({
      user: {
        id: String(res.user_id || '1'),
        name: res.user_name || 'Usuário',
        email,
        role: res.user_role || 'user',
        is_owner: res.is_owner || 0,
        is_superadmin: res.is_superadmin || 0,
        account_type: 'tenant',
        lock_timeout_minutes: res.lock_timeout_minutes || 0,
        profile_name: res.profile_name || '',
        permissions: res.permissions || [],
      } as User,
      isAuthenticated: true,
    });
  },
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('account_type');
    set({ user: null, isAuthenticated: false });
  },
  setUser: (user: User) => {
    set({ user, sessionError: false });
  },
}));
